import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Skeleton,
  Autocomplete,
} from '@mui/material';
import {
  PictureAsPdf,
  TableChart,
  Refresh,
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Inventory as InventoryIcon,
  MoneyOff,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getAllExpenses } from '../firebase/expenseService';
import { getBills } from '../firebase/billingService';
import { getStockTransfers } from '../firebase/stockTransferService';
import { getInventoryItems } from '../firebase/inventoryService';
import { getGRNs } from '../firebase/grnService';
import { getBillingSettings } from '../firebase/settingsService';
import { getReturns } from '../firebase/returnsService';
import LowStockAlertSmall from './LowStockAlertSmall';

const Reports = ({ permissions }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [bills, setBills] = useState([]);
  const [returns, setReturns] = useState([]);
  const [stockTransfers, setStockTransfers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [grns, setGRNs] = useState([]);
  const [companyName, setCompanyName] = useState('Perfume Stories');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Check if user can view profit/loss data
  const canViewProfitLoss = permissions?.userType === 'admin' || permissions?.viewProfitLoss === true;
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    grossRevenue: 0,
    totalReturns: 0,
    receivables: 0,
    creditReceivables: 0,
    codReceivables: 0,
    kokoReceivables: 0,
    kokoChargesTotal: 0,
    mintPayReceivables: 0,
    mintPayChargesTotal: 0,
    cashReceived: 0,
    totalCost: 0,
    grossProfit: 0,
    profitMargin: 0,
    totalExpenses: 0,
    totalLosses: 0,
    totalStockLoss: 0,
    totalPurchases: 0,
    netProfit: 0,
    totalDeliveryRevenue: 0,
    netDeliveryRevenue: 0,
    totalDeliveryRefunds: 0,
    billCount: 0,
    expenseCount: 0,
    purchaseCount: 0,
    // MRP-based metrics
    totalMRP: 0,
    totalDiscounts: 0,
    billDiscounts: 0,
    batchDiscounts: 0,
    otherExpenses: 0,
    mrpNetProfit: 0,
    mrpProfitMargin: 0,
    // Discount tracking
    invoiceDiscounts: 0,
    loyaltyDiscounts: 0,
    batchDiscountsGiven: 0,
    totalDiscountsGiven: 0,
  });

  // Load all data once
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    const [expensesResult, billsResult, returnsResult, transfersResult, inventoryResult, grnsResult, settingsResult] = await Promise.all([
      getAllExpenses(),
      getBills(),
      getReturns(),
      getStockTransfers(),
      getInventoryItems(),
      getGRNs(),
      getBillingSettings(),
    ]);
    if (expensesResult.success) setExpenses(expensesResult.data);
    if (billsResult.success) setBills(billsResult.data);
    if (returnsResult.success) setReturns(returnsResult.data);
    if (transfersResult.success) setStockTransfers(transfersResult.data);
    if (inventoryResult.success) setInventory(inventoryResult.data);
    if (grnsResult.success) setGRNs(grnsResult.data);
    if (settingsResult.success && settingsResult.data?.companyName) {
      setCompanyName(settingsResult.data.companyName);
    }
    setLoading(false);
  };

  const loadBillsAndInvoices = loadAllData;

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadBillsAndInvoices();
    setRefreshing(false);
  };

  // Recalculate metrics when data or filters change
  useEffect(() => {
    if (!loading) {
      const newMetrics = calculateMetrics();
      setMetrics(newMetrics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, customStartDate, customEndDate, expenses, bills, returns, stockTransfers, inventory, grns, selectedProduct, loading]);

  // Date filtering functions
  const getDateRange = (period) => {
    const today = new Date();

    switch (period) {
      case 'today': {
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      case '7days': {
        const start = new Date();
        start.setDate(today.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      case '30days': {
        const start = new Date();
        start.setDate(today.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      case 'custom':
        if (!customStartDate || !customEndDate) return null;
        return {
          start: new Date(customStartDate + 'T00:00:00'),
          end: new Date(customEndDate + 'T23:59:59'),
        };
      default:
        return null;
    }
  };

  const filterByDateRange = (items, dateField = 'date') => {
    const periods = ['today', '7days', '30days', 'custom'];
    const period = periods[tabValue];
    const range = getDateRange(period);

    if (!range) return items;

    return items.filter(item => {
      const raw = item[dateField];
      if (!raw) return false;
      // If it's a plain date string like "2026-04-10", parse as local time
      const itemDate = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)
        ? new Date(raw + 'T00:00:00')
        : new Date(raw);
      return itemDate >= range.start && itemDate <= range.end;
    });
  };

  // Calculate metrics
  const calculateMetrics = () => {
    const filteredExpenses = filterByDateRange(expenses);
    let filteredBills = filterByDateRange(bills);
    const filteredGRNs = filterByDateRange(grns);

    // Apply product filter to bills if a product is selected
    if (selectedProduct) {
      filteredBills = filteredBills.filter(bill =>
        bill.items?.some(item => item.id === selectedProduct.id || item.name === selectedProduct.name)
      );
    }

    // Sales metrics — ALL bills for gross revenue (returns shown separately by return date)
    // Separate product revenue from delivery revenue
    const grossRevenue = filteredBills.reduce((sum, bill) => {
      const delivery = bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0;
      return sum + (bill.total || 0) - delivery;
    }, 0);

    // Total delivery revenue (separate from product revenue)
    const totalDeliveryRevenue = filteredBills.reduce((sum, bill) => {
      return sum + (bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0);
    }, 0);

    // Returns in this period (by return date, not sale date)
    const filteredReturns = filterByDateRange(returns);
    // Separate product returns from delivery charge refunds
    const totalReturns = filteredReturns.reduce((sum, ret) => {
      const deliveryRefunded = ret.deliveryChargeRefunded || 0;
      return sum + (ret.refundAmount || 0) - deliveryRefunded;
    }, 0);
    const totalDeliveryRefunds = filteredReturns.reduce((sum, ret) => {
      return sum + (ret.deliveryChargeRefunded || 0);
    }, 0);

    // Net product revenue = gross product revenue - product returns
    const totalRevenue = grossRevenue - totalReturns;
    // Net delivery revenue = delivery collected - delivery refunded
    const netDeliveryRevenue = totalDeliveryRevenue - totalDeliveryRefunds;

    // Cash received = Paid bills (Cash/Card) — product amount only
    const cashReceived = filteredBills
      .filter(bill => bill.status === 'Paid')
      .reduce((sum, bill) => {
        const delivery = bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0;
        return sum + (bill.total || 0) - delivery;
      }, 0);

    // Credit receivables — product amount only
    const creditReceivables = filteredBills
      .filter(bill => bill.status === 'Pending' && bill.paymentType === 'Credit')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    // COD receivables — includes delivery (full amount owed)
    const codReceivables = filteredBills
      .filter(bill => bill.status === 'Pending' && bill.paymentType === 'COD')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    // Koko receivables
    const kokoReceivables = filteredBills
      .filter(bill => bill.status === 'Koko' || bill.paymentType === 'Koko')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    // Total Koko service charges collected
    const kokoChargesTotal = filteredBills
      .filter(bill => bill.paymentType === 'Koko' && bill.kokoCharge?.amount)
      .reduce((sum, bill) => sum + (bill.kokoCharge.amount || 0), 0);

    // MintPay receivables
    const mintPayReceivables = filteredBills
      .filter(bill => bill.status === 'MintPay' || bill.paymentType === 'MintPay')
      .reduce((sum, bill) => sum + (bill.total || 0), 0);

    // Total MintPay service charges collected
    const mintPayChargesTotal = filteredBills
      .filter(bill => bill.paymentType === 'MintPay' && bill.mintPayCharge?.amount)
      .reduce((sum, bill) => sum + (bill.mintPayCharge.amount || 0), 0);

    // All receivables combined
    const receivables = creditReceivables + codReceivables + kokoReceivables + mintPayReceivables;

    // COGS — all bills by sale date, minus COGS of bills returned in this same period
    const returnedBillIds = new Set(filteredReturns.map(r => r.billId));

    // Build a map of returned item quantities per bill for partial return COGS adjustment
    const returnedItemsMap = {}; // billId -> { itemId -> returnedQty }
    filteredReturns.forEach(ret => {
      if (!returnedItemsMap[ret.billId]) returnedItemsMap[ret.billId] = {};
      (ret.items || []).forEach(item => {
        const id = item.inventoryId || item.id;
        returnedItemsMap[ret.billId][id] = (returnedItemsMap[ret.billId][id] || 0) + (item.quantity || 0);
      });
    });

    const totalCost = filteredBills.reduce((sum, bill) => {
      // If this bill was fully returned in the same period, exclude all COGS
      if (returnedBillIds.has(bill.id) && bill.returnStatus === 'Fully Returned') {
        return sum;
      }

      let billCOGS = 0;
      if (bill.costOfGoodsSold !== undefined) {
        billCOGS = bill.costOfGoodsSold || 0;
      } else {
        billCOGS = (bill.items || []).reduce((itemSum, item) => {
          const invId = item.inventoryId || item.id;
          const inventoryItem = inventory.find(inv => inv.id === invId || inv.name === item.name);
          const costPrice = inventoryItem?.averageCost || inventoryItem?.costPrice || 0;
          const mlFactor = item.isDecant && item.decantML ? item.decantML : 1;
          return itemSum + (costPrice * mlFactor * (item.quantity || 0));
        }, 0);
      }

      // For partial returns in same period — subtract returned items' COGS
      if (returnedBillIds.has(bill.id) && returnedItemsMap[bill.id]) {
        const returnedItems = returnedItemsMap[bill.id];
        const returnedCOGS = (bill.items || []).reduce((itemSum, item) => {
          const invId = item.inventoryId || item.id;
          const returnedQty = returnedItems[invId] || returnedItems[item.id] || 0;
          if (returnedQty <= 0) return itemSum;
          const inventoryItem = inventory.find(inv => inv.id === invId || inv.name === item.name);
          const costPrice = inventoryItem?.averageCost || inventoryItem?.costPrice || 0;
          const mlFactor = item.isDecant && item.decantML ? item.decantML : 1;
          return itemSum + (costPrice * mlFactor * returnedQty);
        }, 0);
        billCOGS = Math.max(0, billCOGS - returnedCOGS);
      }

      return sum + billCOGS;
    }, 0);
    const grossProfit = totalRevenue - totalCost;
    const profitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;

    // Expense metrics
    const totalExpenses = filteredExpenses
      .filter(e => e.categoryType === 'expense')
      .reduce((sum, e) => sum + (parseFloat(e.grandTotal) || 0), 0);

    const totalLosses = filteredExpenses
      .filter(e => e.categoryType === 'loss')
      .reduce((sum, e) => sum + (parseFloat(e.grandTotal) || 0), 0);

    const totalStockLoss = filteredExpenses
      .filter(e => e.affectsInventory)
      .reduce((sum, e) => sum + (parseFloat(e.totalStockLossValue) || 0), 0);

    // Total purchases = GRN grand totals (actual supplier purchases)
    const totalPurchases = filteredGRNs.reduce((sum, g) => sum + (g.grandTotal || 0), 0);

    // Net profit = Gross Profit + Delivery Revenue - Expenses - Losses
    const netProfit = grossProfit + netDeliveryRevenue - totalExpenses - totalLosses;

    // ===== DISCOUNT TRACKING =====
    // Calculate all 3 discount types from bills (not from expenses) — exclude fully returned
    const activeBillsForDiscounts = filteredBills.filter(b => b.returnStatus !== 'Fully Returned');
    const invoiceDiscounts = activeBillsForDiscounts.reduce((sum, bill) => sum + (bill.discount?.amount || 0), 0);
    const loyaltyDiscounts = activeBillsForDiscounts.reduce((sum, bill) => sum + (bill.loyaltyDiscount?.amount || 0), 0);
    const batchDiscountsGiven = activeBillsForDiscounts.reduce((sum, bill) => {
      return sum + (bill.items || []).reduce((itemSum, item) => {
        if (!item.hasBatchDiscounts || !item.batchBreakdown) return itemSum;
        return itemSum + item.batchBreakdown.reduce((batchSum, batch) => {
          if (!batch.hasDiscount || !batch.discount) return batchSum;
          return batchSum + ((batch.originalPrice - batch.price) * batch.quantity);
        }, 0);
      }, 0);
    }, 0);
    const totalDiscountsGiven = invoiceDiscounts + loyaltyDiscounts + batchDiscountsGiven;
    // Calculate Total MRP = Sum of (retail price × quantity sold)
    const totalMRP = filteredBills.reduce((sum, bill) => {
      const billMRP = (bill.items || []).reduce((itemSum, item) => {
        const retailPrice = item.retailPrice || item.price || 0;
        const quantity = item.quantity || 0;
        return itemSum + (retailPrice * quantity);
      }, 0);
      return sum + billMRP;
    }, 0);

    // Collect discounts from bills (bill discount + loyalty discount)
    const billDiscounts = filteredBills.reduce((sum, bill) => {
      const billDiscount = (bill.discount?.amount || 0);
      const loyaltyDiscount = (bill.loyaltyDiscount?.amount || 0);
      return sum + billDiscount + loyaltyDiscount;
    }, 0);

    // Collect batch discounts from expenses (where batchDiscountData exists)
    const batchDiscounts = filteredExpenses
      .filter(e => e.batchDiscountData)
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

    const totalDiscounts = billDiscounts + batchDiscounts;

    // Other expenses (non-discount expenses)
    const otherExpenses = filteredExpenses
      .filter(e => e.categoryType === 'expense' && !e.batchDiscountData)
      .reduce((sum, e) => sum + (parseFloat(e.grandTotal) || 0), 0);

    // MRP-based profit = Total MRP - Total COGS - Total Discounts - Other Expenses
    const mrpNetProfit = totalMRP - totalCost - totalDiscounts - otherExpenses;
    const mrpProfitMargin = totalMRP > 0 ? (mrpNetProfit / totalMRP) * 100 : 0;

    return {
      totalRevenue,
      grossRevenue,
      totalReturns,
      totalDeliveryRevenue,
      netDeliveryRevenue,
      totalDeliveryRefunds,
      receivables,
      creditReceivables,
      codReceivables,
      kokoReceivables,
      kokoChargesTotal,
      mintPayReceivables,
      mintPayChargesTotal,
      cashReceived,
      totalCost,
      grossProfit,
      profitMargin,
      totalExpenses,
      totalLosses,
      totalStockLoss,
      totalPurchases,
      netProfit,
      billCount: filteredBills.length,
      expenseCount: filteredExpenses.length,
      purchaseCount: filteredGRNs.length,
      // MRP metrics
      totalMRP,
      totalDiscounts,
      billDiscounts,
      batchDiscounts,
      otherExpenses,
      mrpNetProfit,
      mrpProfitMargin,
      // Discount tracking
      invoiceDiscounts,
      loyaltyDiscounts,
      batchDiscountsGiven,
      totalDiscountsGiven,
    };
  };

  // Export functions
  const downloadPDF = () => {
    const periods = ['Today', 'Last 7 Days', 'Last 30 Days', 'Custom Period'];
    const periodName = periods[tabValue];

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const rightCol = pageWidth - margin;
    let yPos = margin;

    const fmt = (n) => parseFloat(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const lineRow = (label, value, bold = false, indent = 0) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(10);
      doc.text(label, margin + indent, yPos);
      doc.text(value, rightCol, yPos, { align: 'right' });
      yPos += 6;
    };
    const sectionLine = () => {
      doc.setLineWidth(0.4);
      doc.line(margin, yPos, rightCol, yPos);
      yPos += 5;
    };
    const doubleLine = () => {
      doc.setLineWidth(0.8);
      doc.line(margin, yPos, rightCol, yPos);
      yPos += 1.5;
      doc.line(margin, yPos, rightCol, yPos);
      yPos += 5;
    };

    // ── Header ──────────────────────────────────────────────────────────────
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, margin, yPos);
    yPos += 10;

    doc.setFontSize(14);
    doc.text('PROFIT & LOSS STATEMENT', margin, yPos);
    yPos += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Period: ${periodName}`, margin, yPos);
    doc.text(`Date: ${generatedDate}`, rightCol, yPos, { align: 'right' });
    yPos += 5;
    sectionLine();

    // ── REVENUE ─────────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('REVENUE', margin, yPos);
    yPos += 7;

    lineRow('Gross Sales Revenue', `LKR ${fmt(metrics.grossRevenue)}`);

    if (metrics.totalReturns > 0) {
      lineRow('Less: Returns & Refunds', `(LKR ${fmt(metrics.totalReturns)})`, false, 2);
    }

    lineRow('Net Sales Revenue', `LKR ${fmt(metrics.totalRevenue)}`, true);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`(${metrics.billCount} bills)`, margin + 4, yPos);
    yPos += 6;

    doc.text(`Cash Received: LKR ${fmt(metrics.cashReceived)}`, margin + 4, yPos);
    yPos += 5;
    doc.text(`Receivables: LKR ${fmt(metrics.receivables)}`, margin + 4, yPos);
    yPos += 5;
    doc.text(`  • Credit: LKR ${fmt(metrics.creditReceivables)}`, margin + 8, yPos);
    yPos += 4;
    doc.text(`  • COD: LKR ${fmt(metrics.codReceivables)}`, margin + 8, yPos);
    yPos += 4;
    doc.text(`  • Koko: LKR ${fmt(metrics.kokoReceivables)}`, margin + 8, yPos);
    yPos += 4;
    doc.text(`  • MintPay: LKR ${fmt(metrics.mintPayReceivables)}`, margin + 8, yPos);
    yPos += 5;

    if (metrics.kokoChargesTotal > 0) {
      doc.text(`Koko Service Charges: LKR ${fmt(metrics.kokoChargesTotal)}`, margin + 4, yPos);
      yPos += 5;
    }

    if (metrics.mintPayChargesTotal > 0) {
      doc.text(`MintPay Service Charges: LKR ${fmt(metrics.mintPayChargesTotal)}`, margin + 4, yPos);
      yPos += 5;
    }

    // Delivery Revenue — separate line in PDF
    if (metrics.netDeliveryRevenue > 0) {
      yPos += 2;
      lineRow('Delivery Revenue (COD)', `LKR ${fmt(metrics.netDeliveryRevenue)}`);
      if (metrics.totalDeliveryRefunds > 0) {
        lineRow('Less: Delivery Refunds', `(LKR ${fmt(metrics.totalDeliveryRefunds)})`, false, 2);
      }
    }
    yPos += 2;

    // ── COST OF GOODS SOLD ───────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('COST OF GOODS SOLD', margin, yPos);
    yPos += 7;

    lineRow('Total Cost of Items Sold', `(LKR ${fmt(metrics.totalCost)})`);
    yPos += 2;
    sectionLine();

    // ── GROSS PROFIT ─────────────────────────────────────────────────────────
    lineRow('GROSS PROFIT', `LKR ${fmt(metrics.grossProfit)}`, true);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Margin: ${metrics.profitMargin.toFixed(2)}%`, margin + 4, yPos);
    yPos += 8;

    // ── OPERATING EXPENSES ───────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('OPERATING EXPENSES', margin, yPos);
    yPos += 7;

    lineRow('Total Expenses', `(LKR ${fmt(metrics.totalExpenses)})`);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`(${metrics.expenseCount} transactions)`, margin + 4, yPos);
    yPos += 8;

    // ── LOSSES & WRITE-OFFS ──────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('LOSSES & WRITE-OFFS', margin, yPos);
    yPos += 7;

    lineRow('Total Losses', `(LKR ${fmt(metrics.totalLosses)})`);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Stock Loss: LKR ${fmt(metrics.totalStockLoss)}`, margin + 4, yPos);
    yPos += 8;

    sectionLine();

    // ── TOTAL DEDUCTIONS ─────────────────────────────────────────────────────
    const totalDeductions = metrics.totalExpenses + metrics.totalLosses;
    lineRow('Total Deductions', `(LKR ${fmt(totalDeductions)})`, true, 2);
    yPos += 3;
    doubleLine();

    // ── NET PROFIT ───────────────────────────────────────────────────────────
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    const netColor = metrics.netProfit >= 0 ? [27, 128, 27] : [200, 20, 20];
    doc.setTextColor(netColor[0], netColor[1], netColor[2]);
    doc.text('NET PROFIT', margin, yPos);
    doc.text(`LKR ${fmt(metrics.netProfit)}`, rightCol, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    yPos += 7;
    doubleLine();

    // ── Calculation note ─────────────────────────────────────────────────────
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Calculation: Gross Profit - Operating Expenses - Losses', margin, yPos);
    yPos += 5;
    doc.text(
      `${fmt(metrics.grossProfit)} + ${fmt(metrics.netDeliveryRevenue || 0)} - ${fmt(metrics.totalExpenses)} - ${fmt(metrics.totalLosses)} = ${fmt(metrics.netProfit)}`,
      margin,
      yPos
    );
    yPos += 10;

    // ── Discounts Given (if any) ─────────────────────────────────────────────
    if (metrics.totalDiscountsGiven > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('DISCOUNTS GIVEN', margin, yPos);
      yPos += 7;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text('(For tracking only — already reflected in Net Sales Revenue)', margin + 4, yPos);
      yPos += 6;
      lineRow('Invoice Discounts', `LKR ${fmt(metrics.invoiceDiscounts)}`);
      lineRow('Loyalty Discounts', `LKR ${fmt(metrics.loyaltyDiscounts)}`);
      lineRow('Batch Discounts', `LKR ${fmt(metrics.batchDiscountsGiven)}`);
      sectionLine();
      lineRow('Total Discounts Given', `LKR ${fmt(metrics.totalDiscountsGiven)}`, true);
      yPos += 5;
    }

    // ── Footer ───────────────────────────────────────────────────────────────
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 15, rightCol, pageHeight - 15);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, pageHeight - 9);
    doc.text('Page 1 of 1', rightCol, pageHeight - 9, { align: 'right' });

    doc.save(`${companyName.replace(/\s+/g, '_')}_PL_${periodName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const downloadExcel = () => {
    const periods = ['Today', 'Last 7 Days', 'Last 30 Days', 'Custom Period'];
    const periodName = periods[tabValue];
    const fmt = (n) => `LKR ${parseFloat(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    let csv = `${companyName}\n`;
    csv += `PROFIT & LOSS STATEMENT\n`;
    csv += `Period: ${periodName}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    csv += `\n`;

    csv += `═══════════════════════════════════════════════════════════\n`;
    csv += `PROFIT & LOSS STATEMENT\n`;
    csv += `═══════════════════════════════════════════════════════════\n`;
    csv += `\n`;

    // REVENUE
    csv += `REVENUE\n`;
    csv += `Gross Sales Revenue,${fmt(metrics.grossRevenue)}\n`;
    if (metrics.totalReturns > 0) {
      csv += `Less: Returns & Refunds,(${fmt(metrics.totalReturns)})\n`;
    }
    csv += `Net Sales Revenue,${fmt(metrics.totalRevenue)}\n`;
    csv += `Number of Bills,${metrics.billCount}\n`;
    csv += `  Cash Received,${fmt(metrics.cashReceived)}\n`;
    csv += `  Receivables (Pending Credit/COD/Koko/MintPay),${fmt(metrics.receivables)}\n`;
    csv += `    • Credit,${fmt(metrics.creditReceivables)}\n`;
    csv += `    • COD,${fmt(metrics.codReceivables)}\n`;
    csv += `    • Koko,${fmt(metrics.kokoReceivables)}\n`;
    csv += `    • MintPay,${fmt(metrics.mintPayReceivables)}\n`;
    csv += `  Koko Service Charges Collected,${fmt(metrics.kokoChargesTotal)}\n`;
    csv += `  MintPay Service Charges Collected,${fmt(metrics.mintPayChargesTotal)}\n`;
    csv += `\n`;

    // COGS
    csv += `COST OF GOODS SOLD (COGS)\n`;
    csv += `Total Cost of Items Sold,${fmt(metrics.totalCost)}\n`;
    csv += `(Based on inventory cost prices)\n`;
    csv += `\n`;

    // GROSS PROFIT
    csv += `GROSS PROFIT\n`;
    csv += `Gross Profit (Revenue - COGS),${fmt(metrics.grossProfit)}\n`;
    csv += `Gross Profit Margin,${metrics.profitMargin.toFixed(2)}%\n`;
    csv += `\n`;

    csv += `───────────────────────────────────────────────────────────\n`;
    csv += `\n`;

    // OPERATING EXPENSES
    csv += `OPERATING EXPENSES\n`;
    csv += `Total Expenses,${fmt(metrics.totalExpenses)}\n`;
    csv += `Number of Expense Transactions,${metrics.expenseCount}\n`;
    csv += `(Rent Salaries Utilities etc.)\n`;
    csv += `\n`;

    // LOSSES
    csv += `LOSSES\n`;
    csv += `Total Losses,${fmt(metrics.totalLosses)}\n`;
    csv += `Stock Loss Value,${fmt(metrics.totalStockLoss)}\n`;
    csv += `(Damaged goods Theft Write-offs)\n`;
    csv += `\n`;

    csv += `───────────────────────────────────────────────────────────\n`;
    csv += `\n`;

    // NET PROFIT
    csv += `NET PROFIT (FINAL)\n`;
    csv += `Net Profit,${fmt(metrics.netProfit)}\n`;
    csv += `Formula: Gross Profit + Delivery Revenue - Expenses - Losses\n`;
    csv += `Calculation: ${parseFloat(metrics.grossProfit).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - ${parseFloat(metrics.totalExpenses).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - ${parseFloat(metrics.totalLosses).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} = ${parseFloat(metrics.netProfit).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
    csv += `\n`;

    csv += `═══════════════════════════════════════════════════════════\n`;
    csv += `\n`;

    // DISCOUNTS GIVEN
    if (metrics.totalDiscountsGiven > 0) {
      csv += `DISCOUNTS GIVEN (tracking only — already in Net Sales Revenue)\n`;
      csv += `Invoice Discounts,${fmt(metrics.invoiceDiscounts)}\n`;
      csv += `Loyalty Discounts,${fmt(metrics.loyaltyDiscounts)}\n`;
      csv += `Batch Discounts,${fmt(metrics.batchDiscountsGiven)}\n`;
      csv += `Total Discounts Given,${fmt(metrics.totalDiscountsGiven)}\n`;
      csv += `\n`;
    }

    // ADDITIONAL METRICS
    const filteredTransfers = filterByDateRange(stockTransfers);
    const totalTransferValue = filteredTransfers.reduce((sum, t) => sum + (t.totalValue || 0), 0);
    csv += `ADDITIONAL METRICS\n`;
    csv += `Total Stock Transfers Value,${fmt(totalTransferValue)}\n`;
    csv += `Stock Transfers,${filteredTransfers.length}\n`;
    csv += `\n`;

    // SALES DETAILS — exclude fully returned bills
    const filteredBills = filterByDateRange(bills);
    const filteredReturnsForExcel = filterByDateRange(returns);
    const fullyReturnedBillIds = new Set(
      filteredReturnsForExcel
        .filter(r => r.isFullReturn || r.returnType === 'full')
        .map(r => r.billId)
    );
    const salesBills = filteredBills.filter(b => !fullyReturnedBillIds.has(b.id));

    csv += `SALES DETAILS (exclude fully returned bills)\n`;
    csv += `Invoice #,Date,Customer,Type,Items,Total\n`;
    salesBills.forEach(bill => {
      const customer = (bill.customer || bill.customerName || '').replace(/,/g, ' ');
      csv += `${bill.invoiceNumber || bill.id},${bill.date},${customer},${bill.customerType || 'N/A'},${bill.items?.length || 0},${fmt(bill.total)}\n`;
    });
    csv += `\n`;

    // EXPENSE DETAILS
    const filteredExpenses = filterByDateRange(expenses);
    csv += `EXPENSE DETAILS\n`;
    csv += `Date,Title,Category,Type,Amount,Stock Loss,Grand Total\n`;
    filteredExpenses.forEach(exp => {
      const title = (exp.title || '').replace(/,/g, ' ');
      const catName = (exp.categoryName || '').replace(/,/g, ' ');
      csv += `${exp.date},${title},${catName},${exp.categoryType},${fmt(exp.amount)},${fmt(exp.totalStockLossValue || 0)},${fmt(exp.grandTotal)}\n`;
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName.replace(/\s+/g, '_')}_PL_${periodName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Skeleton variant="text" width={250} height={50} />
            <Skeleton variant="text" width={350} height={30} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" width="100%" height={60} />
        </Card>

        <Grid container spacing={4} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="70%" height={40} />
                  <Skeleton variant="text" width="50%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {!canViewProfitLoss && (
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          backgroundColor: 'error.lighter',
          border: '2px solid',
          borderColor: 'error.main',
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="h6" fontWeight={700} color="#c62828" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have permission to view profit/loss reports. Please contact your administrator.
          </Typography>
        </Box>
      )}

      {canViewProfitLoss && (
        <>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Business Reports
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Comprehensive financial and operational reports
              </Typography>
            </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                color="primary"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={downloadPDF}
                size="small"
                color="primary"
              >
                PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<TableChart />}
                onClick={downloadExcel}
                size="small"
                color="primary"
              >
                Excel
              </Button>
            </Box>
          </Box>

      <LowStockAlertSmall />

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Today" />
          <Tab label="Last 7 Days" />
          <Tab label="Last 30 Days" />
          <Tab label="Custom Period" />
        </Tabs>

        {tabValue === 3 && (
          <Box sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {customStartDate && customEndDate && (
              <Chip
                label={`${customStartDate} to ${customEndDate}`}
                color="primary"
                onDelete={() => {
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
              />
            )}
          </Box>
        )}

        <Box sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            options={inventory}
            getOptionLabel={(option) => `${option.name} (${option.category})`}
            value={selectedProduct}
            onChange={(e, newValue) => setSelectedProduct(newValue)}
            sx={{ width: 300 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Product"
                placeholder="Select a product..."
              />
            )}
          />
          {selectedProduct && (
            <Chip
              label={`${selectedProduct.name} (${selectedProduct.category})`}
              color="primary"
              onDelete={() => setSelectedProduct(null)}
            />
          )}
        </Box>
      </Card>

      {/* ── 4 Summary Cards ─────────────────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Card 1: Total Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp sx={{ color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} color="success.main">
                LKR {(metrics.totalRevenue + (metrics.netDeliveryRevenue || 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Products: LKR {metrics.totalRevenue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                {metrics.netDeliveryRevenue > 0 && ` + Delivery: LKR ${metrics.netDeliveryRevenue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2: Gross Profit */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachMoney sx={{ color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">Gross Profit</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                LKR {metrics.grossProfit.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Margin: {metrics.profitMargin.toFixed(2)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 3: Total Expenses */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingDown sx={{ color: 'error.main' }} />
                <Typography variant="caption" color="text.secondary">Total Expenses</Typography>
              </Box>
              <Typography variant="h5" fontWeight={700} color="error.main">
                LKR {(metrics.totalExpenses + metrics.totalLosses).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Expenses + Losses
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 4: Net Profit */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyOff sx={{ color: metrics.netProfit >= 0 ? 'success.main' : 'error.main' }} />
                <Typography variant="caption" color="text.secondary">Net Profit</Typography>
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                color={metrics.netProfit >= 0 ? 'success.main' : 'error.main'}
              >
                LKR {metrics.netProfit.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                After all deductions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Breakdown Cards ──────────────────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Revenue Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Revenue Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Gross Sales Revenue */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Gross Sales Revenue</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  LKR {metrics.grossRevenue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>

              {/* Returns — only when > 0 */}
              {metrics.totalReturns > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="error.main">Less: Returns &amp; Refunds</Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    (LKR {metrics.totalReturns.toLocaleString('en-LK', { minimumFractionDigits: 2 })})
                  </Typography>
                </Box>
              )}

              {/* Net Sales Revenue */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" fontWeight={700}>Net Sales Revenue</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">
                  LKR {metrics.totalRevenue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>

              {/* Delivery Revenue — only show if > 0 */}
              {metrics.netDeliveryRevenue > 0 && (
                <>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Delivery Revenue (COD)</Typography>
                    <Typography variant="body2" fontWeight={600} color="info.main">
                      LKR {metrics.netDeliveryRevenue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                  {metrics.totalDeliveryRefunds > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="error.main">Less: Delivery Refunds</Typography>
                      <Typography variant="body2" fontWeight={600} color="error.main">
                        (LKR {metrics.totalDeliveryRefunds.toLocaleString('en-LK', { minimumFractionDigits: 2 })})
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ mb: 1 }} />
                </>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Cash Received</Typography>
                <Typography variant="body2" fontWeight={600}>
                  LKR {metrics.cashReceived.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Receivables</Typography>
                <Typography variant="body2" fontWeight={600}>
                  LKR {metrics.receivables.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, pl: 2 }}>
                <Typography variant="caption" color="text.secondary">• Credit</Typography>
                <Typography variant="caption">
                  LKR {metrics.creditReceivables.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, pl: 2 }}>
                <Typography variant="caption" color="text.secondary">• COD</Typography>
                <Typography variant="caption">
                  LKR {metrics.codReceivables.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
                <Typography variant="caption" color="text.secondary">• Koko</Typography>
                <Typography variant="caption">
                  LKR {metrics.kokoReceivables.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
                <Typography variant="caption" color="text.secondary">• MintPay</Typography>
                <Typography variant="caption">
                  LKR {metrics.mintPayReceivables.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Expense Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Expense Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Operational Expenses</Typography>
                  <Typography variant="body2" fontWeight={600} color="warning.main">
                    LKR {metrics.totalExpenses.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Losses</Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    LKR {metrics.totalLosses.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    <InventoryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                    Stock Loss Value
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    LKR {metrics.totalStockLoss.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight={600}>Total Expenses</Typography>
                  <Typography variant="body1" fontWeight={700} color="error.main">
                    LKR {(metrics.totalExpenses + metrics.totalLosses).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Profit Calculation Card (full width) ─────────────────────────── */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Profit Calculation
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Net Sales Revenue</Typography>
            <Typography variant="body2" fontWeight={600}>
              LKR {metrics.totalRevenue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="error.main">Cost of Goods Sold</Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              - LKR {metrics.totalCost.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1" fontWeight={600} color="primary.main">
              Gross Profit
            </Typography>
            <Typography variant="body1" fontWeight={700} color="primary.main">
              LKR {metrics.grossProfit.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({metrics.profitMargin.toFixed(2)}% margin)
              </Typography>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="error.main">Operating Expenses</Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              - LKR {metrics.totalExpenses.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="error.main">Losses</Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              - LKR {metrics.totalLosses.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" fontWeight={700}>NET PROFIT</Typography>
            <Typography
              variant="body1"
              fontWeight={700}
              color={metrics.netProfit >= 0 ? 'success.main' : 'error.main'}
            >
              LKR {metrics.netProfit.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ── Discounts Given Card ─────────────────────────────────────────── */}
      {metrics.totalDiscountsGiven > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Discounts Given
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Invoice Discounts</Typography>
              <Typography variant="body2" fontWeight={600}>
                LKR {metrics.invoiceDiscounts.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Loyalty Discounts</Typography>
              <Typography variant="body2" fontWeight={600}>
                LKR {metrics.loyaltyDiscounts.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2">Batch Discounts</Typography>
              <Typography variant="body2" fontWeight={600}>
                LKR {metrics.batchDiscountsGiven.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight={700}>Total Discounts Given</Typography>
              <Typography variant="body1" fontWeight={700} color="warning.main">
                LKR {metrics.totalDiscountsGiven.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Note: Discounts are already reflected in Net Sales Revenue above — this is for tracking purposes only.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Detailed Tables */}
      <Grid container spacing={4} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Sales Transactions
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterByDateRange(bills)
                      .filter(bill => {
                        if (!selectedProduct) return true;
                        return bill.items?.some(item => item.id === selectedProduct.id || item.name === selectedProduct.name);
                      })
                      .map((bill) => (
                        <TableRow key={bill.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} color="primary">
                              {bill.invoiceNumber || bill.id}
                            </Typography>
                          </TableCell>
                          <TableCell>{bill.date}</TableCell>
                          <TableCell>{bill.customer || bill.customerName || '—'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={bill.customerType || 'N/A'} 
                              size="small"
                              color={bill.customerType === 'Wholesale' ? 'secondary' : 'primary'}
                            />
                          </TableCell>
                          <TableCell>
                            {selectedProduct ? (
                              <Typography variant="body2">
                                {selectedProduct.name} ({selectedProduct.category})
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {bill.items?.length || 0} items
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{bill.items?.length || 0}</TableCell>
                          <TableCell align="right">
                            LKR {(bill.total || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    {filterByDateRange(bills).filter(bill => {
                      if (!selectedProduct) return true;
                      return bill.items?.some(item => item.id === selectedProduct.id || item.name === selectedProduct.name);
                    }).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">
                            {selectedProduct ? `No sales found for ${selectedProduct.name}` : 'No sales in this period'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Expenses & Losses
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Stock Loss</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterByDateRange(expenses).map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell>{expense.date}</TableCell>
                        <TableCell>{expense.title}</TableCell>
                        <TableCell>{expense.categoryName}</TableCell>
                        <TableCell>
                          <Chip 
                            label={expense.categoryType === 'expense' ? 'Expense' : 'Loss'} 
                            size="small"
                            color={expense.categoryType === 'expense' ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          LKR {(expense.amount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">
                          {expense.affectsInventory && (
                            <Typography variant="body2" color="error.main">
                              LKR {(expense.totalStockLossValue || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            LKR {(expense.grandTotal || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filterByDateRange(expenses).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">No expenses in this period</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {tabValue === 3 && (!customStartDate || !customEndDate) && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Please select both start and end dates to view custom period report
        </Alert>
      )}
        </>
      )}
    </Box>
  );
};

export default Reports;
